/**
 * Community-Aware Cache Invalidation Service
 * 
 * Provides intelligent cache invalidation based on community context.
 * Handles community switching, subdomain changes, and community-specific
 * cache management with namespace awareness.
 * 
 * Phase 3 implementation - community-aware cache management
 */

import { QueryClient } from '@tanstack/react-query';
import { invalidateBrandingCache } from './brandingCacheIntegration';
import { orchestrateCacheInvalidation } from './cacheInvalidationOrchestrator';

// Development environment detection
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Interface for community context
 */
export interface CommunityContext {
  id?: string;
  slug?: string;
  hostname?: string;
  subdomain?: string;
}

/**
 * Interface for community-aware invalidation options
 */
export interface CommunityInvalidationOptions {
  invalidateBranding?: boolean;
  invalidateReactQuery?: boolean;
  invalidateLocalStorage?: boolean;
  invalidateSessionStorage?: boolean;
  preserveAuth?: boolean;
  preserveUserPreferences?: boolean;
}

/**
 * Interface for community invalidation result
 */
export interface CommunityInvalidationResult {
  success: boolean;
  communityContext: CommunityContext;
  brandingInvalidated: boolean;
  storageKeysCleared: string[];
  queryKeysCleared: string[];
  errors: string[];
  durationMs: number;
}

/**
 * Get community-specific localStorage keys
 */
function getCommunityLocalStorageKeys(context: CommunityContext): string[] {
  const keys: string[] = [];
  
  // Basic community keys
  if (context.hostname) {
    keys.push(`terrarium_community_branding_v4`); // Hostname-specific branding
  }
  
  if (context.slug) {
    keys.push(`terrarium_community_${context.slug}_navigation`);
    keys.push(`terrarium_community_${context.slug}_settings`);
    keys.push(`terrarium_community_${context.slug}_member_data`);
  }

  if (context.id) {
    keys.push(`terrarium_community_${context.id}_cache`);
    keys.push(`terrarium_community_${context.id}_state`);
  }

  // Onboarding data (community-specific)
  keys.push('terrarium_community_member_onboarding');
  
  return keys;
}

/**
 * Get community-specific React Query keys
 */
function getCommunityQueryKeys(context: CommunityContext): string[] {
  const keys: string[] = [];

  if (context.hostname) {
    keys.push(`branding:${context.hostname}`);
  }

  if (context.slug) {
    keys.push(`community:${context.slug}`);
    keys.push(`community-members:${context.slug}`);
    keys.push(`community-navigation:${context.slug}`);
    keys.push(`community-settings:${context.slug}`);
  }

  if (context.id) {
    keys.push(`community:${context.id}`);
    keys.push(`jobs:${context.id}`);
    keys.push(`events:${context.id}`);
    keys.push(`channels:${context.id}`);
  }

  return keys;
}

/**
 * Clear community-specific localStorage entries
 */
function clearCommunityLocalStorage(
  context: CommunityContext, 
  options: CommunityInvalidationOptions
): string[] {
  const clearedKeys: string[] = [];

  if (!options.invalidateLocalStorage) {
    return clearedKeys;
  }

  try {
    const communityKeys = getCommunityLocalStorageKeys(context);
    
    // Get all localStorage keys to check for matches
    const allKeys = Object.keys(localStorage);
    
    communityKeys.forEach(communityKey => {
      // Handle exact matches
      if (allKeys.includes(communityKey) && localStorage.getItem(communityKey) !== null) {
        localStorage.removeItem(communityKey);
        clearedKeys.push(communityKey);
      }
      
      // Handle pattern matches (e.g., community_slug_*)
      allKeys.forEach(key => {
        if (key.includes(communityKey.replace('*', ''))) {
          localStorage.removeItem(key);
          clearedKeys.push(key);
        }
      });
    });

    // Preserve auth and user preferences if specified
    const preserveKeys: string[] = [];
    if (options.preserveAuth) {
      preserveKeys.push('supabase.auth.token', 'terrarium_auth_state');
    }
    if (options.preserveUserPreferences) {
      preserveKeys.push('terrarium_user_preferences', 'terrarium_theme_preference');
    }

    // Restore preserved keys (this is a simplified approach - in practice, you'd save/restore the values)
    if (isDevelopment && preserveKeys.length > 0) {
      console.log('CommunityCache: Keys preserved from invalidation:', preserveKeys);
    }

    if (isDevelopment && clearedKeys.length > 0) {
      console.log('CommunityCache: Cleared localStorage keys:', clearedKeys);
    }
  } catch (error) {
    console.error('CommunityCache: Failed to clear localStorage:', error);
  }

  return clearedKeys;
}

/**
 * Clear community-specific React Query cache
 */
function clearCommunityQueryCache(
  queryClient: QueryClient,
  context: CommunityContext,
  options: CommunityInvalidationOptions
): string[] {
  const clearedQueryKeys: string[] = [];

  if (!options.invalidateReactQuery) {
    return clearedQueryKeys;
  }

  try {
    const communityQueryKeys = getCommunityQueryKeys(context);

    communityQueryKeys.forEach(queryKeyPattern => {
      if (queryKeyPattern.includes(':')) {
        // Handle specific keys like "branding:hostname"
        const [prefix, identifier] = queryKeyPattern.split(':');
        queryClient.removeQueries({ 
          queryKey: [prefix, identifier] 
        });
        clearedQueryKeys.push(queryKeyPattern);
      } else {
        // Handle prefix-based removal
        queryClient.removeQueries({ 
          predicate: (query) => {
            return Array.isArray(query.queryKey) && 
                   query.queryKey.length > 0 && 
                   query.queryKey[0] === queryKeyPattern;
          }
        });
        clearedQueryKeys.push(`${queryKeyPattern}:*`);
      }
    });

    if (isDevelopment && clearedQueryKeys.length > 0) {
      console.log('CommunityCache: Cleared React Query keys:', clearedQueryKeys);
    }
  } catch (error) {
    console.error('CommunityCache: Failed to clear React Query cache:', error);
  }

  return clearedQueryKeys;
}

/**
 * Orchestrate community-aware cache invalidation
 */
export async function invalidateCommunityCache(
  queryClient: QueryClient,
  context: CommunityContext,
  options: CommunityInvalidationOptions = {},
  reason?: string
): Promise<CommunityInvalidationResult> {
  const startTime = performance.now();
  
  if (isDevelopment) {
    console.log('CommunityCache: Starting community-aware invalidation', { 
      context, 
      options, 
      reason 
    });
  }

  const result: CommunityInvalidationResult = {
    success: false,
    communityContext: context,
    brandingInvalidated: false,
    storageKeysCleared: [],
    queryKeysCleared: [],
    errors: [],
    durationMs: 0,
  };

  try {
    // Set default options
    const finalOptions: CommunityInvalidationOptions = {
      invalidateBranding: true,
      invalidateReactQuery: true,
      invalidateLocalStorage: true,
      invalidateSessionStorage: false,
      preserveAuth: true,
      preserveUserPreferences: true,
      ...options,
    };

    // Invalidate branding cache if requested and hostname is available
    if (finalOptions.invalidateBranding && context.hostname) {
      try {
        const brandingResult = await invalidateBrandingCache(
          queryClient,
          context.hostname,
          `Community cache invalidation: ${reason || 'Unknown'}`
        );
        result.brandingInvalidated = brandingResult.success;
        result.queryKeysCleared.push(...brandingResult.queryKeysCleared);
        if (!brandingResult.success) {
          result.errors.push(...brandingResult.errors);
        }
      } catch (error) {
        result.errors.push(`Branding invalidation failed: ${error}`);
      }
    }

    // Clear community-specific localStorage
    const clearedStorageKeys = clearCommunityLocalStorage(context, finalOptions);
    result.storageKeysCleared.push(...clearedStorageKeys);

    // Clear community-specific React Query cache
    const clearedQueryKeys = clearCommunityQueryCache(queryClient, context, finalOptions);
    result.queryKeysCleared.push(...clearedQueryKeys);

    // Determine overall success
    result.success = result.errors.length === 0;
    
  } catch (error) {
    const errorMsg = `Community cache invalidation failed: ${error}`;
    result.errors.push(errorMsg);
    console.error('CommunityCache:', errorMsg);
  }

  result.durationMs = performance.now() - startTime;
  
  if (isDevelopment) {
    console.log('CommunityCache: Community invalidation completed', {
      ...result,
      context,
      reason
    });
  }

  return result;
}

/**
 * Handle community switching with intelligent cache management
 */
export async function handleCommunitySwitch(
  queryClient: QueryClient,
  fromContext: CommunityContext | null,
  toContext: CommunityContext,
  reason?: string
): Promise<CommunityInvalidationResult> {
  if (isDevelopment) {
    console.log('CommunityCache: Handling community switch', {
      from: fromContext,
      to: toContext,
      reason
    });
  }

  // If switching from a different community, invalidate the old community's cache
  if (fromContext && (
    fromContext.id !== toContext.id ||
    fromContext.hostname !== toContext.hostname ||
    fromContext.slug !== toContext.slug
  )) {
    // Clear previous community cache
    await invalidateCommunityCache(
      queryClient,
      fromContext,
      {
        invalidateBranding: true,
        invalidateReactQuery: true,
        invalidateLocalStorage: true,
        preserveAuth: true,
        preserveUserPreferences: true,
      },
      `Community switch cleanup: ${fromContext.slug || fromContext.id} → ${toContext.slug || toContext.id}`
    );
  }

  // Preload/refresh new community cache if needed
  return invalidateCommunityCache(
    queryClient,
    toContext,
    {
      invalidateBranding: false, // Don't clear new community branding unnecessarily
      invalidateReactQuery: false, // Don't clear new community queries
      invalidateLocalStorage: false, // Don't clear new community storage
      preserveAuth: true,
      preserveUserPreferences: true,
    },
    `Community switch preparation: ${reason || 'Community navigation'}`
  );
}

/**
 * Detect community context from current environment
 */
export function detectCommunityContext(): CommunityContext {
  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];
  
  // Extract community info from URL or hostname
  const context: CommunityContext = {
    hostname,
    subdomain: subdomain !== hostname ? subdomain : undefined,
  };

  // Try to extract from URL path
  const pathMatch = window.location.pathname.match(/^\/c\/([^\/]+)/);
  if (pathMatch) {
    context.slug = pathMatch[1];
  }

  if (isDevelopment) {
    console.log('CommunityCache: Detected community context:', context);
  }

  return context;
}

/**
 * Development utility to inspect community cache state
 */
export function inspectCommunityCacheState(context?: CommunityContext): void {
  if (!isDevelopment) {
    console.warn('CommunityCache: inspect only available in development');
    return;
  }

  const effectiveContext = context || detectCommunityContext();
  const communityKeys = getCommunityLocalStorageKeys(effectiveContext);
  const queryKeys = getCommunityQueryKeys(effectiveContext);

  console.log('CommunityCache: State inspection');
  console.log('- Context:', effectiveContext);
  console.log('- Expected localStorage keys:', communityKeys);
  console.log('- Expected query keys:', queryKeys);

  // Check which keys actually exist
  const existingStorageKeys = communityKeys.filter(key => 
    localStorage.getItem(key) !== null
  );
  console.log('- Existing localStorage keys:', existingStorageKeys);

  // Show sample data for existing keys
  existingStorageKeys.forEach(key => {
    try {
      const value = localStorage.getItem(key);
      console.log(`- ${key}:`, value ? JSON.parse(value) : value);
    } catch {
      console.log(`- ${key}:`, localStorage.getItem(key));
    }
  });
}